/**
 * skm-helpers.js — SKM-MODERN JS 헬퍼 (PR-J1 2026-05-20 신설)
 *
 * SKM-MODERN 디자인 철학 #1 (통일) + #3 (재사용).
 *
 * 원장님 명령: "디자인 통일해주고 재사용 가능하게 작업"
 *
 * 본 파일 — 165 Swal.fire 호출의 표준 helper 통일.
 * skm-modern 토큰 + 한글 button text + dark mode 자동 적용.
 *
 * ─── 아키텍처 ─────────────────────────────────────────────────
 * - window.skm 전역 노출 (IIFE 안전 캡슐)
 * - Swal 미로드/지연 환경에서도 window.skm native fallback 보장
 * - 모든 helper 가 내부 fire() 함수 경유 → DEFAULT_OPTIONS 자동 병합
 * - PR-J2~J15 에서 본 helper 호출로 ~410건 마이그레이션 (단순 3-arg + simple object-form)
 * - ES5 호환 (var / function() / Object.assign) — old browser 호환
 *
 * ─── 공개 메서드 8종 ─────────────────────────────────────────
 *   skm.alert(icon, title, opts)       — 일반 알림 (icon 명시)
 *   skm.success(title, opts)           — 성공 단축 (icon=success 자동)
 *   skm.error(title, opts)             — 에러 단축 (icon=error)
 *   skm.warning(title, opts)           — 경고 단축 (icon=warning)
 *   skm.info(title, opts)              — 정보 단축 (icon=info)
 *   skm.confirm(title, opts)           — 확인 다이얼로그 (Yes/No, question icon)
 *   skm.confirmDelete(title, opts)     — 삭제 확인 (danger 톤 — confirmButtonColor=var(--skm-danger))
 *   skm.toast(icon, title, opts)       — 우상단 비침해 토스트 (3초 후 자동 닫힘)
 *
 * ─── 사용 예시 ───────────────────────────────────────────────
 *   skm.success('저장 완료');
 *   skm.error('저장 실패', { html: msg });
 *   skm.confirm('정말 삭제?').then(function (r) { if (r.isConfirmed) {...} });
 *   skm.confirmDelete().then(function (r) { ... });   // 한글 '삭제' 버튼 + danger 톤
 *   skm.toast('success', '복사 완료');
 *
 * ─── 상세 명세서 ─────────────────────────────────────────────
 * docs/frontend-modernization-roadmap.md §SKM-MODERN Design System Spec
 */
(function (window) {
    'use strict';

    function assign(target) {
        target = target || {};
        for (var i = 1; i < arguments.length; i += 1) {
            var source = arguments[i] || {};
            Object.keys(source).forEach(function (key) {
                target[key] = source[key];
            });
        }
        return target;
    }

    function htmlToText(value) {
        var text = String(value == null ? '' : value);
        return text
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<[^>]+>/g, '')
            .replace(/&nbsp;/g, ' ')
            .trim();
    }

    function dialogMessage(options) {
        options = options || {};
        var title = htmlToText(options.title);
        var body = htmlToText(options.text || options.html || '');
        if (title && body) {
            return title + '\n\n' + body;
        }
        return title || body || '';
    }

    function resolvedDialogResult(isConfirmed) {
        var payload = { isConfirmed: !!isConfirmed, value: !!isConfirmed };
        if (window.Promise && window.Promise.resolve) {
            return window.Promise.resolve(payload);
        }
        return {
            then: function (callback) {
                if (callback) {
                    callback(payload);
                }
                return this;
            },
            catch: function () {
                return this;
            },
        };
    }

    /**
     * skm-modern 표준 옵션 (skm-modern 디자인 시스템).
     * confirmButtonColor: var(--skm-primary)
     * 한글 button text
     */
    var DEFAULT_OPTIONS = {
        // 다크 테마 기본(§UI/UX #6). SweetAlert2 v11.14+ 는 data-swal2-theme 미지정 시 light 로 떠서
        // 공통 다크 CSS(@sweetalert2/theme-dark, [data-swal2-theme="dark"] 스코프)가 활성화되지 않았다.
        // theme: 'dark' 로 명시 → 이미 로드된 공통 다크 테마(검정 배경/흰 글자)가 적용된다(개별 호출에서 override 가능).
        theme: 'dark',
        confirmButtonColor: 'var(--skm-primary)',
        cancelButtonColor: 'var(--skm-muted)',
        confirmButtonText: '확인',
        cancelButtonText: '취소',
    };

    /**
     * Swal.fire 호출 — 표준 옵션 자동 적용.
     */
    function fire(options) {
        var merged = assign({}, DEFAULT_OPTIONS, options);
        if (window.Swal && window.Swal.fire) {
            return window.Swal.fire(merged);
        }

        var message = dialogMessage(merged);
        if (merged.toast) {
            if (window.console && window.console.info) {
                window.console.info('[skm.toast]', message);
            }
            return resolvedDialogResult(true);
        }
        if (merged.showCancelButton) {
            return resolvedDialogResult(window.confirm(message || '확인하시겠습니까?'));
        }
        if (message) {
            window.alert(message);
        }
        return resolvedDialogResult(true);
    }

    /**
     * 텍스트 클립보드 복사 — Clipboard API 우선, 미지원/실패 시 execCommand fallback.
     * 복사 후 버튼 라벨을 잠시 "복사됨"으로 바꿔 시각 피드백.
     */
    function copyToClipboard(text, btnEl) {
        function feedback() {
            if (!btnEl) {
                return;
            }
            var orig = btnEl.innerHTML;
            btnEl.innerHTML = '<span class="skm-bs5-icon fa fa-check" aria-hidden="true"></span> 복사됨';
            setTimeout(function () { btnEl.innerHTML = orig; }, 1500);
        }
        function fallback() {
            try {
                var ta = document.createElement('textarea');
                ta.value = text;
                ta.style.position = 'fixed';
                ta.style.top = '-9999px';
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            } catch (e) { /* noop */ }
        }
        if (window.navigator && window.navigator.clipboard && window.navigator.clipboard.writeText) {
            window.navigator.clipboard.writeText(text).then(feedback).catch(function () {
                fallback();
                feedback();
            });
        } else {
            fallback();
            feedback();
        }
    }

    var skm = {
        /**
         * 일반 알림 — icon 명시
         * @param {string} icon  success / error / warning / info / question
         * @param {string} title
         * @param {object} options  Swal 옵션 (html / footer / showCancelButton 등)
         */
        alert: function (icon, title, options) {
            return fire(assign({ icon: icon, title: title }, options || {}));
        },

        /**
         * 확인 다이얼로그 (Yes/No)
         */
        confirm: function (title, options) {
            return fire(assign({
                icon: 'question',
                title: title,
                showCancelButton: true,
            }, options || {}));
        },

        /**
         * 성공 단축
         */
        success: function (title, options) {
            return fire(assign({ icon: 'success', title: title }, options || {}));
        },

        /**
         * 에러 단축
         */
        error: function (title, options) {
            return fire(assign({ icon: 'error', title: title }, options || {}));
        },

        /**
         * 경고 단축
         */
        warning: function (title, options) {
            return fire(assign({ icon: 'warning', title: title }, options || {}));
        },

        /**
         * 정보 단축
         */
        info: function (title, options) {
            return fire(assign({ icon: 'info', title: title }, options || {}));
        },

        /**
         * 토스트 — 화면 오른쪽 위 비침해 알림
         */
        toast: function (icon, title, options) {
            return fire(assign({
                icon: icon,
                title: title,
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
            }, options || {}));
        },

        /**
         * 삭제 확인 — 빈번한 case
         */
        confirmDelete: function (title, options) {
            return fire(assign({
                icon: 'warning',
                title: title || '정말 삭제하시겠습니까?',
                text: '삭제된 데이터는 복구할 수 없습니다.',
                showCancelButton: true,
                confirmButtonText: '삭제',
                confirmButtonColor: 'var(--skm-danger)',
            }, options || {}));
        },

        /**
         * 복사 가능한 에러 — 드래그 선택(user-select) + "오류 내용 복사" 버튼
         * (Clipboard API + execCommand fallback). 카드 단말기 결제 실패처럼 운영자가
         * 오류 원문을 카드사/지원팀에 그대로 전달해야 하는 케이스용.
         * 일반 skm.error 동작은 그대로 둔다(전역 회귀 차단).
         * @param {string} title
         * @param {string} message  HTML 허용(<br> 등). 복사 시에는 태그 제거 + 줄바꿈 변환.
         */
        copyableError: function (title, message, options) {
            var raw = String(message == null ? '' : message);
            var copyText = raw.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').trim();
            var html =
                '<div class="skm-copyable-error">' + raw + '</div>' +
                '<button type="button" id="skmCopyErrorBtn" ' +
                'class="skm-btn skm-btn-secondary skm-btn-sm skm-copyable-error__btn">' +
                '<span class="skm-bs5-icon fa fa-copy" aria-hidden="true"></span> 오류 내용 복사</button>';
            return fire(assign({
                icon: 'error',
                title: title,
                html: html,
                didOpen: function () {
                    var btn = document.getElementById('skmCopyErrorBtn');
                    if (btn) {
                        btn.addEventListener('click', function () { copyToClipboard(copyText, btn); });
                    }
                },
            }, options || {}));
        },
    };

    // 전역 노출
    window.skm = window.skm || {};
    Object.keys(skm).forEach(function (k) {
        if (typeof window.skm[k] === 'undefined') {
            window.skm[k] = skm[k];
        }
    });
})(window);
